import "./styles.scss";
import "react-draft-wysiwyg/dist/nextjs-draft-wysiwyg.css";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "context/app";
import FileDropZone from "components/FileDropZone";

import { UserMessage, StatusMessage, MessageSubmitter } from "./components";

import useResponsive from "libs/helpers/useResponsive";

const mainClass = "task-view-chat";
const minimumOverflow = 210;
const edgeFocusDistance = 96;

const getFocusProgress = (scrollTop, overflow) => {
  if (scrollTop < edgeFocusDistance)
    return (scrollTop / edgeFocusDistance) * 0.5;

  const distanceFromBottom = overflow - scrollTop;

  if (distanceFromBottom < edgeFocusDistance)
    return 1 - (distanceFromBottom / edgeFocusDistance) * 0.5;

  return 0.5;
};

const Chat = ({
  data,
  file_progress,
  sendMessage,
  editMessage,
  isMessageSending,
  isCompletelyDisabled,
}) => {
  const { t } = useTranslation();
  const { user: user_data } = useApp();
  const { max } = useResponsive();

  const messages = data?.messages;
  const messages_count = useRef(null);
  const bodyRef = useRef(null);
  const messagesRef = useRef(null);
  const [activeMessageIndex, setActiveMessageIndex] = useState(null);
  const [editingMessageIds, setEditingMessageIds] = useState([]);
  const [fileDrop, setFileDrop] = useState(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const isUploading = file_progress !== undefined && file_progress !== null;
  const isFileDropDisabled =
    isCompletelyDisabled || isUploading || isMessageSending;
  const fileDropMessageId =
    editingMessageIds[editingMessageIds.length - 1] ?? null;

  const handleEditModeChange = useCallback((id, isEditing) => {
    setEditingMessageIds((ids) => {
      const otherIds = ids.filter((messageId) => messageId !== id);
      return isEditing ? [...otherIds, id] : otherIds;
    });
  }, []);

  const handleFilesDrop = (files) => {
    setFileDrop({
      messageId: fileDropMessageId,
      files,
    });
  };

  const handleFilesDropHandled = useCallback(() => setFileDrop(null), []);

  useEffect(() => {
    const count = messages_count.current;

    if (count === null || count !== messages?.length) {
      messages_count.current = messages?.length;

      const bodyEl = bodyRef.current;

      if (bodyEl) bodyEl.scrollTop = bodyEl.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const bodyEl = bodyRef.current;
    const messagesEl = messagesRef.current;

    if (!max.sm || !bodyEl || !messagesEl) {
      setActiveMessageIndex(null);
      return undefined;
    }

    const updateActiveMessage = () => {
      const overflow = bodyEl.scrollHeight - bodyEl.clientHeight;
      const messageElements = Array.from(messagesEl.children);

      if (overflow < minimumOverflow || messageElements.length === 0) {
        setActiveMessageIndex(null);
        return;
      }

      const scrollTop = Math.min(Math.max(bodyEl.scrollTop, 0), overflow);
      const bodyRect = bodyEl.getBoundingClientRect();
      const focusPosition =
        bodyRect.top + bodyEl.clientHeight * getFocusProgress(scrollTop, overflow);
      const visibleMessages = messageElements.reduce((visible, messageEl, index) => {
        const messageRect = messageEl.getBoundingClientRect();
        const visibleTop = Math.max(messageRect.top, bodyRect.top);
        const visibleBottom = Math.min(messageRect.bottom, bodyRect.bottom);

        if (visibleBottom > visibleTop)
          visible.push({ index, visibleTop, visibleBottom });

        return visible;
      }, []);

      if (visibleMessages.length === 0) {
        setActiveMessageIndex(null);
        return;
      }

      const closestMessage = visibleMessages.reduce((closest, message) => {
        const distance = Math.max(
          message.visibleTop - focusPosition,
          focusPosition - message.visibleBottom,
          0,
        );
        const closestDistance = Math.max(
          closest.visibleTop - focusPosition,
          focusPosition - closest.visibleBottom,
          0,
        );

        return distance < closestDistance ? message : closest;
      });

      setActiveMessageIndex(closestMessage.index);
    };

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateActiveMessage)
        : null;

    updateActiveMessage();
    bodyEl.addEventListener("scroll", updateActiveMessage, { passive: true });
    window.addEventListener("resize", updateActiveMessage);
    resizeObserver?.observe(bodyEl);
    resizeObserver?.observe(messagesEl);

    return () => {
      bodyEl.removeEventListener("scroll", updateActiveMessage);
      window.removeEventListener("resize", updateActiveMessage);
      resizeObserver?.disconnect();
    };
  }, [max.sm, messages]);

  const messages_parsed = messages?.map((item, index) =>
    index === 0
      ? {
          ...item,
          template: 0,
        }
      : item,
  );
  const status_messages = messages_parsed?.filter((item) => !!item?.template);
  const last_status = status_messages?.[status_messages?.length - 1];

  return (
    <FileDropZone
      className={mainClass}
      disabled={isFileDropDisabled}
      onFilesDrop={handleFilesDrop}
      onDraggingStateChange={setIsDraggingFiles}
    >
      <h2>{t("Activity")}</h2>
      <div className={`${mainClass}__body`} ref={bodyRef}>
        <div className={`${mainClass}__body-mask`} />
        <div
          id="chat-messages"
          className={`${mainClass}__body-messages`}
          ref={messagesRef}
        >
          {messages_parsed?.map(
            (
              { _id, text, user, template, created_at, updated_at, files },
              index,
            ) =>
              template !== undefined && template !== null ? (
                <StatusMessage
                  key={_id}
                  template={template}
                  date={created_at}
                  user={{
                    avatar: user?.photo,
                    name: user?.name,
                  }}
                  isHighlighted={
                    last_status?._id === _id && user_data?.type === "client"
                  }
                  isInViewport={
                    activeMessageIndex === index ? true : undefined
                  }
                />
              ) : (
                <UserMessage
                  key={[_id, text]}
                  id={_id}
                  date={created_at}
                  edited_date={updated_at}
                  user={{
                    avatar: user?.photo,
                    name: user?.name,
                    type: user?.type,
                  }}
                  message={text}
                  attachments={files}
                  me={user?._id === user_data?._id}
                  file_progress={file_progress}
                  editMessage={editMessage}
                  isMessageSending={isMessageSending}
                  isCompletelyDisabled={isCompletelyDisabled}
                  onEditModeChange={handleEditModeChange}
                  isFileDropTarget={
                    isDraggingFiles &&
                    !isFileDropDisabled &&
                    fileDropMessageId === _id
                  }
                  droppedFiles={
                    fileDrop?.messageId === _id ? fileDrop.files : undefined
                  }
                  onFilesDropHandled={handleFilesDropHandled}
                  isLast={messages_parsed?.length - 1 === index}
                  isInViewport={
                    activeMessageIndex === index ? true : undefined
                  }
                />
              ),
          )}
        </div>
      </div>
      {!isCompletelyDisabled && (
        <div className={`${mainClass}__footer`}>
          <MessageSubmitter
            file_progress={file_progress}
            sendMessage={sendMessage}
            isMessageSending={isMessageSending}
            droppedFiles={
              fileDrop?.messageId === null ? fileDrop.files : undefined
            }
            onFilesDropHandled={handleFilesDropHandled}
          />
        </div>
      )}
    </FileDropZone>
  );
};

export default Chat;
